import { useQuery } from '@tanstack/react-query';
import { getSelfDashboard, getFriendDashboard, ViewTier, TimeWindow, DashboardResponse } from '../services/dashboardApi';
import { useAuth } from '../../auth/hooks/useAuth';

type Mode = 'self' | 'friend';

export interface UseDashboardDataParams {
  mode: Mode;
  tier?: ViewTier;       // required for self
  friendId?: string;     // required for friend
  window: TimeWindow;
  enabled?: boolean;
}

export interface UseDashboardDataResult {
  data: DashboardResponse['data'] | null;
  meta: DashboardResponse['meta'] | null;
  isLoading: boolean;
  isFetching: boolean;
  error: Error | null;
  refetch: () => void;
}

/**
 * Hook to fetch dashboard data for self or friend using React Query
 * 
 * Implements:
 * - Separate query keys for self vs friend to avoid cache conflicts
 * - Skip API call for 'basic' tier (frontend-only tier)
 * Rebuild polling is handled by usePrivateCacheRebuildStatus.
 */
export function useDashboardData(params: UseDashboardDataParams): UseDashboardDataResult {
  const { mode, tier, friendId, window, enabled = true } = params;
  const { user, session } = useAuth();
  
  // Tweak D: Skip API call for 'basic' tier in self mode (frontend-only tier)
  const isBasicTier = mode === 'self' && tier === 'basic';
  
  const hasRequiredParams =
    (mode === 'self' && tier && tier !== 'basic') ||
    (mode === 'friend' && friendId);
    
  const shouldEnable = Boolean(enabled && hasRequiredParams && !isBasicTier && session?.access_token);
  
  // Tweak C: Separate query keys for self vs friend to avoid cache conflicts
  const queryKey = mode === 'self'
    ? ['dashboard', 'self', user?.id, tier, window]
    : ['dashboard', 'friend', friendId, window];
  
  const { data: response, isLoading, isFetching, error, refetch } = useQuery<DashboardResponse, Error>({
    queryKey,
    queryFn: async (): Promise<DashboardResponse> => {
      if (mode === 'self') {
        return getSelfDashboard({ window, tier: tier!, token: session!.access_token });
      } else {
        return getFriendDashboard({ friendId: friendId!, window, token: session!.access_token });
      }
    },
    enabled: shouldEnable,
    refetchInterval: (query) => {
      if (mode !== 'friend') return false;
      return query.state.data?.meta?.refresh_needed === true ? 1500 : false;
    },
    refetchIntervalInBackground: false,
    staleTime: 60_000,
    placeholderData: (previous) => previous,
  });
  
  if (isBasicTier) {
    return { 
      data: null, 
      meta: null, 
      isLoading: false, 
      isFetching: false, 
      error: null, 
      refetch: () => {} 
    };
  }
  
  return {
    data: response?.data ?? null,
    meta: response?.meta ?? null,
    isLoading,
    isFetching,
    error: error ?? null,
    refetch,
  };
}
